import * as React from 'react'
import { formatRate, formatSalary, splitEmployeeCount } from '../extra/formatters'
import { COMPANY_STATUS_TABLE_COLUMN_WIDTHS, SEGMENT_TABLE_COLUMN_WIDTHS } from '../extra/tableColumnWidths'
import TableColgroup from './TableColgroup'
import TableEmptyMessage from './TableEmptyMessage'

const formatSegmentName = (name) => {
  const trimmed = (name ?? '').trim()
  return trimmed || '単一セグメント'
}

/**
 * @param {{
 *   submitterRows: import('../extra/types').SubmitterRow[]
 *   segmentRows: { name: string; count: string }[]
 *   segmentTotal: { name: string; count: string }
 *   salaryUnit: 'yen' | 'thousand-yen'
 *   onSalaryUnitChange: (unit: 'yen' | 'thousand-yen') => void
 *   employeeSortDir: 'asc' | 'desc' | null
 *   onSortByEmployees: () => void
 *   onResetSort: () => void
 * }} props
 */
const CompanyStatusSection = ({
  submitterRows,
  segmentRows,
  segmentTotal,
  salaryUnit,
  onSalaryUnitChange,
  employeeSortDir,
  onSortByEmployees,
  onResetSort
}) => {
  const salaryHeader = salaryUnit === 'yen' ? '平均年間給与(円)' : '平均年間給与(千円)'
  const sortArrow = employeeSortDir === 'asc' ? '↑' : employeeSortDir === 'desc' ? '↓' : '↑↓'
  const hasSubmitterData = (submitterRows ?? []).length > 0
  const hasSegmentData = (segmentRows ?? []).length > 0
  const submittingCompanyRow = (submitterRows ?? []).find((row) => row.kind === 'submit')
  const resolvedSegmentRows = hasSegmentData
    ? segmentRows
    : submittingCompanyRow
      ? [{ name: '単一セグメント', count: submittingCompanyRow.employeesDisplay }]
      : []
  const resolvedSegmentTotal = hasSegmentData
    ? segmentTotal
    : submittingCompanyRow
      ? { name: '合計', count: submittingCompanyRow.employeesDisplay }
      : segmentTotal
  const shouldShowSegmentTable = hasSubmitterData && resolvedSegmentRows.length > 0

  return (
    <div className="mb-8 px-12">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold m-0 p-0">② 提出会社の状況 / ③ 最大人員会社の状況</h3>
        {hasSubmitterData ? (
          <div className="flex items-center gap-2">
            <label htmlFor="salary-unit-select" className="text-base">
              金額単位：
            </label>
            <select
              id="salary-unit-select"
              className="text-base px-2 py-1 border border-chrofy-border rounded"
              value={salaryUnit}
              onChange={(e) => onSalaryUnitChange(e.target.value)}
            >
              <option value="yen">円</option>
              <option value="thousand-yen">千円</option>
            </select>
          </div>
        ) : null}
      </div>
      {!hasSubmitterData ? (
        <TableEmptyMessage />
      ) : (
        <table
          className="securities-report-indicators__table securities-report-indicators__table--company"
          id="company-status-table"
        >
          <TableColgroup widths={COMPANY_STATUS_TABLE_COLUMN_WIDTHS} />
          <thead>
            <tr>
              <th align="center">
                会社名
                <button
                  type="button"
                  onClick={onResetSort}
                  className="reset-sort-btn"
                  title="並び替えを解除して既定の並びに戻す"
                  aria-label="並び替えを解除"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 12a9 9 0 1 0 3-6.7" />
                    <polyline points="3 4 3 10 9 10" />
                  </svg>
                </button>
              </th>
              <th align="center">会社区分</th>
              <th align="center" className="sortable-header" onClick={onSortByEmployees}>
                従業員数(名) <span className={`sort-arrow${employeeSortDir ? '' : ' is-inactive'}`}>{sortArrow}</span>
              </th>
              <th align="center">臨時従業員数(名)</th>
              <th align="center">平均年齢(歳)</th>
              <th align="center">平均勤続年数(年)</th>
              <th align="center">{salaryHeader}</th>
              <th align="center">平均年間給与の対前事業年度増減率（％）</th>
            </tr>
          </thead>
          <tbody>
            {submitterRows.map((row) => {
              const emp = splitEmployeeCount(row.employeesDisplay)
              return (
                <tr key={row.name} data-kind={row.kind}>
                  <td className="company-name-col" title={row.name}>
                    {row.name}
                  </td>
                  <td align="center">{row.kindLabel}</td>
                  <td align="center">{emp.main}</td>
                  <td align="center">{emp.temp}</td>
                  <td align="center">{row.age}</td>
                  <td align="center">{row.tenure}</td>
                  <td className="salary-cell" align="center">
                    {formatSalary(row.salary, salaryUnit)}
                  </td>
                  <td align="center">{formatRate(row.rate)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {shouldShowSegmentTable ? (
        <table className="securities-report-indicators__table securities-report-indicators__table--segment">
          <TableColgroup widths={SEGMENT_TABLE_COLUMN_WIDTHS} />
          <thead>
            <tr>
              <th align="center">セグメントの名称</th>
              <th align="center">従業員数(名)</th>
              <th align="center">臨時従業員数(名)</th>
            </tr>
          </thead>
          <tbody>
            {resolvedSegmentRows.map((row, index) => {
              const count = splitEmployeeCount(row.count)
              const segmentName = formatSegmentName(row.name)
              return (
                <tr key={row.name?.trim() ? row.name : `segment-${index}`}>
                  <td>{segmentName}</td>
                  <td align="center">{count.main}</td>
                  <td align="center">{count.temp}</td>
                </tr>
              )
            })}
            {(() => {
              const totalCount = splitEmployeeCount(resolvedSegmentTotal.count)
              return (
                <tr className="total-row">
                  <th className="total-cell" align="center">
                    {resolvedSegmentTotal.name}
                  </th>
                  <th className="total-cell" align="center">
                    {totalCount.main}
                  </th>
                  <th className="total-cell" align="center">
                    {totalCount.temp}
                  </th>
                </tr>
              )
            })()}
          </tbody>
        </table>
      ) : null}
    </div>
  )
}

export default CompanyStatusSection
